import { get, hash } from '@ember/helper';
import { on } from '@ember/modifier';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed, set } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismCard,
  PrismIcon,
  PrismSelectInput,
  PrismTabsContainer,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import MyAndMarketOtbChartIllustration from 'lumen/assets/images/market-insight/upsell/my-and-market-otb-chart.svg?raw';
import MyOtbChartIllustration from 'lumen/assets/images/market-insight/upsell/my-otb-chart.svg?raw';
import GeneralDropdownsDropdownArrowButton from 'lumen/components/general/dropdowns/dropdown-arrow-button';
import GeneralEmptyStateOccupancy from 'lumen/components/general/empty-state-occupancy';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import HotelMarketInsightChartsChartMarketDemand from 'lumen/components/hotel/market-insight/charts/chart-market-demand';
import HotelMarketInsightChartsChartOtbOverview from 'lumen/components/hotel/market-insight/charts/chart-otb-overview';
import HotelMarketInsightChartsChartWowChange from 'lumen/components/hotel/market-insight/charts/chart-wow-change';
import HotelMarketInsightTabsTabSearches from 'lumen/components/hotel/market-insight/tabs/tab-searches';
import hasFeature from 'lumen/helpers/has-feature';
import scrollIntoView from 'lumen/modifiers/scroll-into-view';
import { getTranslationKeysForBanner } from 'lumen/utils/components/general/empty-state-otb-banner';
import { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';
import {
  ACTIVE_FILTERS,
  DETAIL_PANE_DETAIL_SECTION_CARD_IDS,
  OTB_TABS,
  SEARCHES_TABS,
} from 'lumen/utils/constants/market-insight-constants';
import {
  OTB_SS_ENTRY_POINTS,
  OTB_SS_VIA_QP_VALUES,
} from 'lumen/utils/constants/otb-selfservice-constants';
import otbOutOfSync from 'lumen/utils/otb-out-of-sync';

const OTB_CARD_OPEN_LOCALSTORAGE_KEY = 'isOTBCardOpen';

interface HotelMarketInsightOverviewSignature {
  Args: {
    isMarketInsightFull: unknown;
    isOverviewLoading: unknown;
    isSearchesCardExpanded: unknown;
    model: unknown;
    overviewRange: unknown;
    refDaysAgo: unknown;
    removeScrollTo: unknown;
    scrollTo: unknown;
    setActiveFilters: unknown;
    setDateAggregation: unknown;
    setFocusedDetailSection: unknown;
    toggleSearchesCard: unknown;
    updateOTBTab: unknown;
    updateSearchesDetailsItem: unknown;
    updateSearchesTab: unknown;
  };
}

export default class HotelMarketInsightOverview extends Component<HotelMarketInsightOverviewSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare miSearches: Services['mi-searches'];
  @service declare miSelections: Services['mi-selections'];

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  @tracked isOTBCardOpen =
    // @ts-expect-error: Incorrect type
    JSON.parse(this.localStorage.getItem(OTB_CARD_OPEN_LOCALSTORAGE_KEY)) ??
    true;

  @tracked scrollToSearches = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get activeOTBTabId() {
    const { isOTBTabActive, isWoWTabActive } = this.miSelections;

    if (isOTBTabActive) {
      return OTB_TABS.MY_OTB;
    }

    if (isWoWTabActive) {
      return OTB_TABS.WOW_CHANGE;
    }

    return OTB_TABS.MY_OTB;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get activeSearchesTabId() {
    const {
      isFlightBusinessSearchesTabActive,
      isFlightSearchesTabActive,
      isHotelBusinessSearchesTabActive,
      isHotelSearchesTabActive,
    } = this.miSelections;

    if (isFlightSearchesTabActive) {
      return SEARCHES_TABS.FLIGHTS;
    }
    if (isFlightBusinessSearchesTabActive) {
      return SEARCHES_TABS.FLIGHTSGDS;
    }
    if (isHotelSearchesTabActive) {
      return SEARCHES_TABS.HOTELS;
    }
    if (isHotelBusinessSearchesTabActive) {
      return SEARCHES_TABS.HOTELSGDS;
    }

    return SEARCHES_TABS.FLIGHTS;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get areFlightBusinessSearchesSupportedForTheCurrentDestination() {
    return this.miSearches
      ?.areFlightBusinessSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get areFlightLeisureSearchesSupportedForTheCurrentDestination() {
    return this.miSearches
      ?.areFlightLeisureSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get areHotelBusinessSearchesSupportedForTheCurrentDestination() {
    return this.miSearches
      ?.areHotelBusinessSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get areHotelLeisureSearchesSupportedForTheCurrentDestination() {
    return this.miSearches
      ?.areHotelLeisureSearchesSupportedForTheCurrentDestination;
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dateAggregationOptions() {
    return [
      {
        id: DATE_AGGREGATION.DAY,
        label: this.intl.t('general.day').toString(),
      },
      {
        id: DATE_AGGREGATION.WEEK,
        label: this.intl.t('general.week').toString(),
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get emptyStateOccupancyImgHtml() {
    if (this.access.hasFeature('otb.market_benchmark_eligible')) {
      return MyAndMarketOtbChartIllustration;
    }

    return MyOtbChartIllustration;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHistoricalOTB() {
    const lastWeek = this.hotelAndMarketService
      .getLocalTime()
      .subtract(1, 'week');
    // @ts-expect-error: Incorrect type
    return this.pmsFirstCompleteStayDateWithDefault.isSameOrBefore(
      lastWeek,
      'day',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSearches() {
    return (
      this.areFlightLeisureSearchesSupportedForTheCurrentDestination ||
      this.areFlightBusinessSearchesSupportedForTheCurrentDestination ||
      this.areHotelLeisureSearchesSupportedForTheCurrentDestination ||
      this.areHotelBusinessSearchesSupportedForTheCurrentDestination
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  @computed('args.model.isLoading')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.model?.isLoading;
  }

  @computed('args.model.data.occupancies.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otbOutOfSyncUtil() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { occupancies } = this.args.model.data;

    return otbOutOfSync(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      occupancies,
      this.hotelAndMarketService,
      this.intl,
      this.access,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsFirstCompleteStayDateWithDefault() {
    return this.hotelAndMarketService.hotel
      ?.pmsFirstCompleteStayDateWithDefault;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchTabTitles() {
    const searchTabTitles = {};

    const areHotelBusinessSearchesSupported =
      this.areHotelBusinessSearchesSupportedForTheCurrentDestination;
    const areFlightBusinessSearchesSupported =
      this.areFlightBusinessSearchesSupportedForTheCurrentDestination;

    if (
      areHotelBusinessSearchesSupported &&
      areFlightBusinessSearchesSupported
    ) {
      // @ts-expect-error: Incorrect type
      searchTabTitles[SEARCHES_TABS.FLIGHTS] = this.intl.t(
        'market_insight.searches.flights.metaota',
      );
      // @ts-expect-error: Incorrect type
      searchTabTitles[SEARCHES_TABS.FLIGHTSGDS] = this.intl.t(
        'market_insight.searches.flights.gds',
      );
      // @ts-expect-error: Incorrect type
      searchTabTitles[SEARCHES_TABS.HOTELS] = this.intl.t(
        'market_insight.searches.hotels.metaota',
      );
      // @ts-expect-error: Incorrect type
      searchTabTitles[SEARCHES_TABS.HOTELSGDS] = this.intl.t(
        'market_insight.searches.hotels.gds',
      );
    } else if (
      areHotelBusinessSearchesSupported ||
      areFlightBusinessSearchesSupported
    ) {
      if (areHotelBusinessSearchesSupported) {
        // @ts-expect-error: Incorrect type
        searchTabTitles[SEARCHES_TABS.FLIGHTS] = `${this.intl.t(
          'market_insight.searches.flights',
        )} (${this.intl.t('general.meta')}/${this.intl.t('general.ota')}/${this.intl.t(
          'general.GDS',
        )})`;
        // @ts-expect-error: Incorrect type
        searchTabTitles[SEARCHES_TABS.HOTELS] = this.intl.t(
          'market_insight.searches.hotels.metaota',
        );
        // @ts-expect-error: Incorrect type
        searchTabTitles[SEARCHES_TABS.HOTELSGDS] = this.intl.t(
          'market_insight.searches.hotels.gds',
        );
      } else {
        // @ts-expect-error: Incorrect type
        searchTabTitles[SEARCHES_TABS.FLIGHTS] = this.intl.t(
          'market_insight.searches.flights.metaota',
        );
        // @ts-expect-error: Incorrect type
        searchTabTitles[SEARCHES_TABS.FLIGHTSGDS] = this.intl.t(
          'market_insight.searches.flights.gds',
        );
        // @ts-expect-error: Incorrect type
        searchTabTitles[SEARCHES_TABS.HOTELS] = `${this.intl.t(
          'market_insight.searches.hotels',
        )} (${this.intl.t('general.meta')}/${this.intl.t('general.ota')}/${this.intl.t(
          'general.GDS',
        )})`;
      }
    } else {
      // @ts-expect-error: Incorrect type
      searchTabTitles[SEARCHES_TABS.FLIGHTS] = this.intl.t(
        'market_insight.searches.flights',
      );
      // @ts-expect-error: Incorrect type
      searchTabTitles[SEARCHES_TABS.HOTELS] = this.intl.t(
        'market_insight.searches.hotels',
      );
    }

    return searchTabTitles;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedDateAggregation() {
    const { dateAggregation } = this.miSelections;

    return this.dateAggregationOptions.find(({ id }) => id === dateAggregation);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedDays() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { occupancies } = this.args.model.data;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      occupancies
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .map((occupancy) => occupancy.day)
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .sort((a, b) => a.id - b.id)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleOTBTabIds() {
    if (!this.hasHistoricalOTB) {
      return [];
    }

    return [OTB_TABS.MY_OTB, OTB_TABS.WOW_CHANGE];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleSearchesTabIds() {
    const ids = [];

    if (this.areFlightLeisureSearchesSupportedForTheCurrentDestination) {
      ids.push(SEARCHES_TABS.FLIGHTS);
    }

    if (this.areFlightBusinessSearchesSupportedForTheCurrentDestination) {
      ids.push(SEARCHES_TABS.FLIGHTSGDS);
    }

    if (this.areHotelLeisureSearchesSupportedForTheCurrentDestination) {
      ids.push(SEARCHES_TABS.HOTELS);
    }

    if (this.areHotelBusinessSearchesSupportedForTheCurrentDestination) {
      ids.push(SEARCHES_TABS.HOTELSGDS);
    }

    return ids;
  }

  @action
  // @ts-expect-error: Incorrect type
  dateAggregationKeyChanged(dateAggregation): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.args.setDateAggregation(dateAggregation.id);
  }

  @action
  // @ts-expect-error: Incorrect type
  onChartClicked(e, detailSection = null): void {
    const { isDailyOverview, isWeeklyOverview } = this.miSelections;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    const dateClicked = dayjs.utc(e.point.x);

    const hasDataForDay =
      isDailyOverview &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      !e.point.day?.mostRecentDailySummary?.isDestinationDemandIndexMissing;
    const hasDataForWeek =
      isWeeklyOverview &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      !e.point.day?.mostRecentWeeklySummary?.isDestinationDemandIndexMissing;

    if (hasDataForDay || hasDataForWeek) {
      let filterId = '';
      let filterValue = '';

      if (isDailyOverview) {
        // If it's daily select a day
        filterId = ACTIVE_FILTERS.DAY;
        filterValue = dateClicked.format('YYYY-MM-DD');
      } else {
        // If it's a week, select a range
        filterId = ACTIVE_FILTERS.RANGE;
        const startDate = this.miSelections.getStartDateInRange(
          dateClicked.startOf('isoWeek'),
        );
        const endDate = this.miSelections.getEndDateInRange(
          dateClicked.endOf('isoWeek'),
        );
        filterValue = `${startDate.format('YYYY-MM-DD')}:${endDate.format('YYYY-MM-DD')}`;
      }

      // @ts-expect-error: Incorrect type
      this.args.setActiveFilters(filterId, filterValue);

      if (detailSection) {
        // @ts-expect-error: Incorrect type
        this.args.setFocusedDetailSection(detailSection);

        // Set the currently selected searchType tab in a separate query param
        if (detailSection === DETAIL_PANE_DETAIL_SECTION_CARD_IDS.SEARCHES) {
          // @ts-expect-error: Incorrect type
          this.args.updateSearchesDetailsItem(this.activeSearchesTabId);
        }
      }
    }
  }

  @action
  setScrollIntoView(): void {
    if (this.args.scrollTo === 'searches') {
      this.scrollToSearches = true;
      // @ts-expect-error: Incorrect type
      this.args.removeScrollTo();
    }
  }

  @action
  toggleOTBCard(): void {
    // Toggling the card can cause a scrollbar to appear
    // Which causes the width to decline
    // But it does not fire a resize event
    // Firing one explicitly makes the chart reflow
    // Otherwise the width will be wrong
    // https://developer.mozilla.org/en-US/docs/Web/Guide/Events/Creating_and_triggering_events#The_old-fashioned_way
    const event = document.createEvent('Event');
    event.initEvent('resize', true, true);
    window.dispatchEvent(event);

    set(this, 'isOTBCardOpen', !this.isOTBCardOpen);
    this.localStorage.setItem(
      OTB_CARD_OPEN_LOCALSTORAGE_KEY,
      JSON.stringify(this.isOTBCardOpen),
    );
  }

  <template>
    {{! @glint-nocheck }}
    {{! template-lint-disable no-duplicate-id require-presentational-children }}
    <div class="mi-overview" data-test-id="mi-overview">
      {{#unless @isMarketInsightFull}}
        <section class="mb-1000">
          <PrismBanner
            @bannerMessage={{t "market_insight.light.banner.message"}}
            @bannerStyle="warning"
            @bannerTitle={{t "market_insight.light.banner.title"}}
          />
        </section>
      {{/unless}}
      <div class="section-title mt-0">
        <div class="flex items-center justify-between">
          <div class="text-neutral-emphasis mr-200 flex items-center">
            <h2>{{t "market-insight.header.title"}}
              <span
                class="text-300--regular"
                data-test-id="mi-overview-section-title"
              >
                {{this.miSelections.selectedRefDaysAgo.label}}
              </span>
            </h2>
          </div>
          <div class="text-neutral-subdued ml-300 flex items-center">
            <p class="text-300--regular mr-100">{{t "parity.view_by"}}:</p>
            <PrismSelectInput
              @horizontalPosition="right"
              @onChange={{this.dateAggregationKeyChanged}}
              @options={{this.dateAggregationOptions}}
              @value={{this.selectedDateAggregation}}
              data-test-id="date-aggregation-option-market-outlook"
            />
          </div>
        </div>
      </div>

      <PrismCard class="ic-product-tour-demand-forecast mb-500">
        <:header as |Header|>
          <Header.title>
            {{t "demand.demand_forecast"}}
          </Header.title>
        </:header>
        <:content>
          <section class="market-demand-card">
            <div class="market-demand-chart" data-test-id="market-demand-chart">
              {{#unless @model.isLoading}}
                <HotelMarketInsightChartsChartMarketDemand
                  @isMarketInsightFull={{@isMarketInsightFull}}
                  @model={{@model}}
                  @onChartClicked={{this.onChartClicked}}
                  @refDaysAgo={{@refDaysAgo}}
                  @setActiveFilters={{@setActiveFilters}}
                />
              {{/unless}}
            </div>
          </section>
        </:content>
      </PrismCard>

      {{#if this.access.hasOTBView}}
        <PrismCard
          @contentLayout="noPadding"
          class="mb-800"
          data-test-id="market-insight-occupancy"
        >
          <:header as |Header|>
            <div
              class="button--unstyled flex items-center justify-between"
              role="button"
              {{on "click" this.toggleOTBCard}}
            >
              <Header.title>
                {{t "general.occupancy"}}
              </Header.title>
              <div class="mr-100 flex items-center">
                {{#if this.otbOutOfSyncUtil.isOtbOutOfSync}}
                  <GeneralIconWithTooltip
                    @iconName="sync-disabled"
                    @side="top"
                    @tooltipContent={{this.otbOutOfSyncUtil.otbAndMarketOtbUpdatedString}}
                  />
                {{/if}}
                <GeneralDropdownsDropdownArrowButton
                  @isOpen={{this.isOTBCardOpen}}
                />
              </div>
            </div>
          </:header>
          <:content>
            {{#if this.isOTBCardOpen}}
              <div class="mb-400">
                {{#if @isOverviewLoading}}
                  <section class="card-tile--loading"></section>
                {{else}}
                  {{#if (hasFeature "pms.otb.view")}}
                    {{#if this.visibleOTBTabIds.length}}
                      <PrismTabsContainer
                        @activeTabId={{this.activeOTBTabId}}
                        @onSelectTab={{@updateOTBTab}}
                        @visibleTabIds={{this.visibleOTBTabIds}}
                        as |TabsContainer|
                      >
                        <TabsContainer.header as |Header|>
                          <Header.tab @id={{OTB_TABS.MY_OTB}}>
                            {{t "rates.my_OTB"}}
                          </Header.tab>
                          <Header.tab
                            @id={{OTB_TABS.WOW_CHANGE}}
                            data-test-id="occupancy-wow-change"
                          >
                            {{t "general.daysago" count=@refDaysAgo}}
                          </Header.tab>
                        </TabsContainer.header>
                        <TabsContainer.content as |Content|>
                          <Content.tab @id={{OTB_TABS.MY_OTB}}>
                            <HotelMarketInsightChartsChartOtbOverview
                              @isDailyOverview={{this.miSelections.isDailyOverview}}
                              @offsetTooltipHeight={{@isMarketInsightFull}}
                              @onChartClicked={{this.onChartClicked}}
                              @sortedDays={{this.sortedDays}}
                            />
                          </Content.tab>
                          <Content.tab @id={{OTB_TABS.WOW_CHANGE}}>
                            <HotelMarketInsightChartsChartWowChange
                              @isDailyOverview={{this.miSelections.isDailyOverview}}
                              @offsetTooltipHeight={{@isMarketInsightFull}}
                              @onChartClicked={{this.onChartClicked}}
                              @refDaysAgo={{@refDaysAgo}}
                              @sortedDays={{this.sortedDays}}
                            />
                          </Content.tab>
                        </TabsContainer.content>
                      </PrismTabsContainer>
                    {{else}}
                      <HotelMarketInsightChartsChartOtbOverview
                        @isDailyOverview={{this.miSelections.isDailyOverview}}
                        @offsetTooltipHeight={{@isMarketInsightFull}}
                        @onChartClicked={{this.onChartClicked}}
                        @sortedDays={{this.sortedDays}}
                      />
                    {{/if}}
                  {{else}}
                    <div
                      class="empty-state-occupancy-chart-container flex items-center justify-center"
                    >
                      {{#let
                        (getTranslationKeysForBanner
                          OTB_SS_ENTRY_POINTS.MI_OCCUPANCY_CHART
                          (hasFeature "otb.market_benchmark_eligible")
                        )
                        as |translationKeys|
                      }}
                        <GeneralEmptyStateOccupancy
                          @imgHtml={{this.emptyStateOccupancyImgHtml}}
                          @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.MI_OCCUPANCY_CHART}}
                          @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.MARKET_INSIGHT}}
                          @stateDescription={{t translationKeys.message}}
                          @stateTitle={{t translationKeys.title}}
                        />
                      {{/let}}
                    </div>
                  {{/if}}
                {{/if}}
              </div>
            {{/if}}
          </:content>
        </PrismCard>
      {{/if}}
      {{#if this.hasSearches}}
        <div class="section-title">
          <div class="flex items-center justify-between">
            <div class="text-neutral-emphasis mr-200 flex items-center">
              <h2>{{t "market_insight.search_traffic"}}</h2>
            </div>
            <div class="section-title-group date-aggregation-dropdown ml-300">
              <p class="text-300--regular mr-100">{{t "parity.view_by"}}:</p>
              <PrismSelectInput
                @horizontalPosition="right"
                @onChange={{this.dateAggregationKeyChanged}}
                @options={{this.dateAggregationOptions}}
                @value={{this.selectedDateAggregation}}
              />
            </div>
          </div>
        </div>
        <PrismCard
          @contentLayout="noPadding"
          class="ic-product-tour-searches-in-the-last-days mb-800"
          {{scrollIntoView shouldScroll=this.scrollToSearches}}
        >
          <:header as |Header|>
            <div
              class="button--unstyled flex items-center justify-between"
              role="button"
              {{on "click" @toggleSearchesCard}}
            >
              <Header.title>
                {{t "market_insight.searches_in_the_last_days" count=7}}
              </Header.title>
              <div class="mr-100">
                <GeneralDropdownsDropdownArrowButton
                  @isOpen={{@isSearchesCardExpanded}}
                />
              </div>
            </div>
          </:header>
          <:content>
            {{#if @isSearchesCardExpanded}}
              {{#if @isOverviewLoading}}
                <section class="card-tile--loading"></section>
              {{else}}
                <PrismTabsContainer
                  @activeTabId={{this.activeSearchesTabId}}
                  @onSelectTab={{@updateSearchesTab}}
                  @visibleTabIds={{this.visibleSearchesTabIds}}
                  as |TabsContainer|
                >
                  <TabsContainer.header as |Header|>
                    <Header.tab @id={{SEARCHES_TABS.FLIGHTS}}>
                      <span class="flex items-center gap-200">
                        <PrismIcon @color="inherit" @name="plane" />
                        {{get this.searchTabTitles SEARCHES_TABS.FLIGHTS}}
                      </span>
                    </Header.tab>
                    <Header.tab @id={{SEARCHES_TABS.FLIGHTSGDS}}>
                      <span class="flex items-center gap-200">
                        <PrismIcon @color="inherit" @name="plane" />
                        {{get this.searchTabTitles SEARCHES_TABS.FLIGHTSGDS}}
                      </span>
                    </Header.tab>
                    <Header.tab @id={{SEARCHES_TABS.HOTELS}}>
                      <span class="flex items-center gap-200">
                        <PrismIcon
                          @color="inherit"
                          @name="business-type-hotel"
                        />
                        {{get this.searchTabTitles SEARCHES_TABS.HOTELS}}
                      </span>
                    </Header.tab>
                    <Header.tab @id={{SEARCHES_TABS.HOTELSGDS}}>
                      <span class="flex items-center gap-200">
                        <PrismIcon
                          @color="inherit"
                          @name="business-type-hotel"
                        />
                        {{get this.searchTabTitles SEARCHES_TABS.HOTELSGDS}}
                      </span>
                    </Header.tab>
                  </TabsContainer.header>
                  <TabsContainer.content as |Content|>
                    <Content.tab @id={{SEARCHES_TABS.FLIGHTS}}>
                      <HotelMarketInsightTabsTabSearches
                        @params={{hash
                          isLoading=this.isLoading
                          onChartClicked=this.onChartClicked
                          overviewRange=@overviewRange
                          searchsummaries=@model.data.searchsummaries
                        }}
                      />
                    </Content.tab>
                    <Content.tab @id={{SEARCHES_TABS.FLIGHTSGDS}}>
                      <HotelMarketInsightTabsTabSearches
                        @params={{hash
                          isLoading=this.isLoading
                          onChartClicked=this.onChartClicked
                          overviewRange=@overviewRange
                          searchsummaries=@model.data.searchsummaries
                        }}
                      />
                    </Content.tab>
                    <Content.tab @id={{SEARCHES_TABS.HOTELS}}>
                      <HotelMarketInsightTabsTabSearches
                        @params={{hash
                          isLoading=this.isLoading
                          onChartClicked=this.onChartClicked
                          overviewRange=@overviewRange
                          searchsummaries=@model.data.searchsummaries
                        }}
                      />
                    </Content.tab>
                    <Content.tab @id={{SEARCHES_TABS.HOTELSGDS}}>
                      <HotelMarketInsightTabsTabSearches
                        @params={{hash
                          isLoading=this.isLoading
                          onChartClicked=this.onChartClicked
                          overviewRange=@overviewRange
                          searchsummaries=@model.data.searchsummaries
                        }}
                      />
                    </Content.tab>
                  </TabsContainer.content>
                </PrismTabsContainer>
              {{/if}}
            {{/if}}
          </:content>
        </PrismCard>
      {{/if}}
    </div>
  </template>
}
